import { InvalidUrlError } from '../../errors/errors';
import { BaseGQLService } from '../../services/BaseGQLService';
import { FilterOperator, SorterSet as GQLSorterSet } from '../../types/gql-types';
import { CursorInfo, PaginationResult } from '../../types/pagination';
import { ModelType } from '../../utils/GQLIdEncoder';
import { SshHandshakeMapper } from '../mappers/SshHandshakeMapper';
import { SshHandshake } from '../models/SshHandshake';

export const SSH_HANDSHAKE_FRAGMENT = `
id
initiatorIp
initiatorPort
targetIp
targetPort
timestamp
status
clientSupportedKexAlgs
serverSupportedKexAlgs
selectedKexAlg
clientSupportedServerHostkeyAlgs
serverSupportedServerHostkeyAlgs
selectedServerHostkeyAlg
clientSupportedMacC2sAlgs
serverSupportedMacC2sAlgs
selectedMacC2sAlg
clientSupportedMacS2cAlgs
serverSupportedMacS2cAlgs
selectedMacS2cAlg
clientSupportedSymencC2sAlgs
serverSupportedSymencC2sAlgs
selectedSymencC2sAlg
clientSupportedSymencS2cAlgs
serverSupportedSymencS2cAlgs
selectedSymencS2cAlg
clientProtocolVersion
serverProtocolVersion
clientSoftwareVersion
serverSoftwareVersion
clientSoftwareComments
serverSoftwareComments
`;

export const GET_SSH_HANDSHAKES_QUERY = (variant: ModelType) => `
query (
  $id: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filters: SshHandshakesFilter,
  $sorters: [SshHandshakesSorter],
) {
  node(id: $id) {
    ... on ${variant === ModelType.Report ? ModelType.ReportDone : variant} {
      sshHandshakes(
        first: $first,
        last: $last,
        after: $after,
        before: $before,
        filters: $filters,
        sorters: $sorters,
      ) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            ${SSH_HANDSHAKE_FRAGMENT}
          }
        }
      }
    }
  }
}
`;

export class SshHandshakesRepository extends BaseGQLService {
  private readonly mapper = new SshHandshakeMapper();

  async getList(
    variant: ModelType,
    variantId: number,
    cursor: CursorInfo,
    filters: FilterOperator | null,
    sorters: GQLSorterSet[]
  ): Promise<PaginationResult<SshHandshake>> {
    return this.query(
      GET_SSH_HANDSHAKES_QUERY(variant),
      {
        id: this.gqlIdEncoder.encode(variant, variantId),
        ...this.mapCursorInfo(cursor),
        filters,
        sorters,
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find ${variant} ${variantId}`);
        }
        const { edges, pageInfo, totalCount } = data.node.sshHandshakes;
        return {
          data: edges.map(({ node }: any) => this.mapper.mapGQLModel(node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }
}
